// Deal Pipeline spec §2.2 / §2.3 — primary-action computation for the deal
// detail header. Returns the contextually-relevant CTA based on stage,
// sub-state, and the current user's role + permissions.
//
// The mapping mirrors the primary-action table in spec §2.3 and the workflow gate
// rules in spec §6.1. Permission gating is double-enforced server side
// (DealService); these strings only drive button visibility / disabled
// state in the header.

import {
  DEAL_PERMISSIONS,
  type DealDetailDto,
  type PreApprovalDocumentRequestDto,
} from "@/types/deals";

export type PrimaryActionId =
  | "mark-review-complete"
  | "mark-ready-for-assignment"
  | "advance-to-preapproval"
  // Pre-Approval milestones — header points at the relevant page section
  // since milestone advancement is owned by the page itself.
  | "open-applications"
  | "open-letters"
  | "open-document-requests"
  | "send-to-underwriting"
  | "advance-to-underwriting"
  | "clone";

export interface PrimaryAction {
  id: PrimaryActionId;
  label: string;
  /** When set, the button renders disabled and the reason surfaces in a
   *  tooltip. Lets the caller render a contextual hint without branching
   *  on the stage themselves. */
  disabled?: boolean;
  disabledReason?: string;
  /** Tooltip surfaced even when the button is enabled — used to flag
   *  workaround actions (e.g. recovering an inconsistent state). */
  tooltip?: string;
}

interface AuthLike {
  hasPermission: (perm: string) => boolean;
  userId: string | undefined;
}

/**
 * Computes the primary action for the deal detail header. Returns null
 * when the current user has no contextual action — e.g. a viewer looking
 * at an Underwriting-stage deal, where the module's UI offers no CTA
 * (the underwriting module owns it). The header still shows the overflow
 * menu in that case.
 *
 * Pre-Approval milestones — sub-states are derived from milestone data
 * (Applications → Letters → Documents → ReadyToSubmit); the page itself
 * owns advancement, so the header's CTA only points the user at the
 * right section.
 */
export function getPrimaryAction(
  deal: DealDetailDto,
  auth: AuthLike,
  _documentRequests: PreApprovalDocumentRequestDto[] = [],
): PrimaryAction | null {
  const { hasPermission, userId } = auth;
  const isC1 = !!userId && deal.assignedC1Id === userId;
  const isB1 = !!userId && deal.assignedB1Id === userId;

  // Terminal stages — anyone with Deals.Create can clone.
  if (deal.stage === "Declined" || deal.stage === "Withdrawn") {
    if (hasPermission(DEAL_PERMISSIONS.Create)) {
      return { id: "clone", label: "Clone Deal" };
    }
    return null;
  }

  // The two recovery CTAs ("Advance to Pre-Approval", "Advance to
  // Underwriting") call PATCH /api/deals/{id}/stage, which DealsController
  // gates on Deals.AdvanceStage — so they show to that permission, not to
  // Deals.Assign.
  const canAdvanceStage = hasPermission(DEAL_PERMISSIONS.AdvanceStage);

  if (deal.stage === "Intake") {
    const canUpdate = hasPermission(DEAL_PERMISSIONS.Update);

    switch (deal.subState) {
      case "New":
        // Intake/New has no header CTA — the live affordance lives in the
        // intake checklist on the Intake tab.
        return null;
      case "GatheringParties":
        if (isC1 && canUpdate) {
          return { id: "mark-review-complete", label: "Mark Review Complete" };
        }
        return null;
      case "IntakeReview":
        if (isC1 && canUpdate) {
          return { id: "mark-ready-for-assignment", label: "Mark Ready for Assignment" };
        }
        return null;
      case "ReadyForAssignment":
        // Inconsistent-state recovery: B1 is already assigned but the deal
        // never advanced (pre-fix legacy data, or a partial assignment that
        // failed mid-flow). Offer a manual advance instead of suggesting
        // the user re-assign the same B1.
        if (deal.assignedB1Id && canAdvanceStage) {
          return {
            id: "advance-to-preapproval",
            label: "Advance to Pre-Approval",
            tooltip:
              "B1 is assigned but the deal hasn't advanced. Click to advance manually.",
          };
        }
        // No header CTA for the standard assign path — the live "Assign
        // to B1" affordance lives in the intake checklist footer.
        return null;
      case "AssignedToB1":
        // No header CTA. Nothing writes this sub-state: assigning B1 at
        // ReadyForAssignment advances the deal to PreApproval/Applications
        // (DealAssignmentService.AssignB1Async).
        return null;
      default:
        return null;
    }
  }

  if (deal.stage === "PreApproval") {
    // Pre-Approval milestones — the page itself owns the four-section
    // workflow (Applications → Letters → Documents → Send to Underwriting).
    // The detail-header CTA points the user at the right page section so
    // they can advance the right milestone next.
    switch (deal.subState) {
      case "Applications":
        if (isB1) {
          return {
            id: "open-applications",
            label: "Update Applications",
            tooltip:
              "Open the Pre-Approval page and mark Borrower + Seller applications Received.",
          };
        }
        return null;
      case "Letters":
        if (isB1) {
          return {
            id: "open-letters",
            label: "Update Conditional Approval Letters",
            tooltip:
              "Open the Pre-Approval page and mark the Borrower Conditional Loan Approval and the Seller Conditional Approval Received-Signed.",
          };
        }
        return null;
      case "Documents":
        if (isB1) {
          return {
            id: "open-document-requests",
            label: "Update Document Requests",
            tooltip:
              "Open the Pre-Approval page and clear the remaining required documents.",
          };
        }
        return null;
      case "ReadyToSubmit":
        // Inconsistent-state recovery: A1 already assigned but the deal
        // never advanced to Underwriting (pre-fix legacy data, or a partial
        // submission that failed mid-flow). Offer a manual advance instead
        // of asking the user to re-assign the same A1.
        if (deal.assignedA1Id && canAdvanceStage) {
          return {
            id: "advance-to-underwriting",
            label: "Advance to Underwriting",
            tooltip:
              "A1 is assigned but the deal hasn't advanced. Click to advance manually.",
          };
        }
        if (isB1) {
          return {
            id: "send-to-underwriting",
            label: "Send to Underwriting",
            tooltip:
              "Open the Pre-Approval page and complete the final submission triad.",
          };
        }
        return null;
      default:
        return null;
    }
  }

  // Underwriting / Closing / Closed — module's surface is read-only here.
  return null;
}
